
import { useEffect, useRef } from "react";
import { useSupabaseSync } from "@/hooks/useSupabaseSync";
import { saveToSupabaseThenLocal } from "@/utils/investors/databaseUtils";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext"; 
import { toast } from "sonner";
import { markSyncStarted, markSyncCompleted } from "@/utils/investors/processing/processingState";

export const useInvestorSync = (
  dataLoaded: boolean,
  investors: any[],
  contacts: any[],
  processedIds: Set<string>,
  relevanceContext: string,
  businessInfo: string,
  isSaving: React.MutableRefObject<boolean>,
  isSyncing: React.MutableRefObject<boolean>,
  setInvestors: (investors: any[]) => void,
  setContacts: (contacts: any[]) => void,
  setBusinessInfo: (info: string) => void,
  setRelevanceContext: (context: string) => void
) => {
  const { isAuthenticated, supabaseSession } = useGoogleAuth(); 
  const { 
    dataLoaded: supabaseDataLoaded,
    syncContextToSupabase,
    loadFromSupabase
  } = useSupabaseSync();
  
  // Ref to track if sync is disabled during batch operations
  const syncDisabledRef = useRef<boolean>(false);
  
  // Track loaded investor count to prevent syncing empty data
  const lastLoadedInvestorCount = useRef<number>(0);
  
  // Function to check if sync is disabled in batch mode
  const checkSyncDisabled = () => {
    // Batch processing can disable sync temporarily
    return syncDisabledRef.current;
  };
  
  // Set up initial load from Supabase - only once when authenticated
  useEffect(() => {
    // Only load initial data if authenticated
    if (isAuthenticated && dataLoaded && supabaseSession?.user?.id) {
      // Force an immediate check for data from the database when authenticated
      if (!isSyncing.current && !isSaving.current) {
        // Skip check if batch processing is happening
        if (!checkSyncDisabled()) {
          console.log("Starting optimized database load operations - marking sync started");
          markSyncStarted();
          
          // Initial load - only once
          loadFromSupabase().then(data => {
            if (data) {
              // Critical check: Only load data if it's not empty
              if (data.investors && data.investors.length > 0) {
                // Save the count for later comparison
                lastLoadedInvestorCount.current = data.investors.length;
                
                // Track how many processed investors in the loaded data
                const processedCount = data.investors.filter((inv: any) => inv.enrichedWith).length;
                console.log(`Loaded ${data.investors.length} investors from database using optimized structure (${processedCount} processed)`);
                
                setInvestors(data.investors);
              } else {
                console.log("Skipped loading empty investors array from database");
              }
              
              if (data.contacts && data.contacts.length > 0) {
                setContacts(data.contacts);
              }
              
              if (data.businessInfo) {
                setBusinessInfo(data.businessInfo);
              }
              
              if (data.relevanceContext) {
                setRelevanceContext(data.relevanceContext);
              }
              
              console.log("Initial data load: loaded from database using optimized structure");
              toast.success("Data loaded from your account");
            } else {
              console.log("No data returned from database load");
            }
            
            // Mark sync as complete
            markSyncCompleted();
          }).catch(error => {
            console.error("Error loading from database:", error);
            markSyncCompleted();
          });
        } else {
          console.log("Skipping database load - sync is disabled");
        }
      } else {
        console.log("Skipping database load - sync or save operation already in progress");
      }
    }
  }, [
    isAuthenticated, 
    dataLoaded,
    supabaseSession,
    loadFromSupabase, 
    setInvestors, 
    setContacts, 
    setBusinessInfo, 
    setRelevanceContext
  ]);
  
  // Function to set sync disabled state during batch processing
  const setSyncDisabled = (disabled: boolean) => {
    syncDisabledRef.current = disabled;
    console.log(`Sync disabled during batch: ${disabled}`);
  };
  
  return { 
    supabaseDataLoaded,
    setSyncDisabled,
    lastLoadedInvestorCount: lastLoadedInvestorCount.current
  };
};
